import { brand, neutral } from '@/design/tokens';

/**
 * The app's accent follows the selected project's colour.
 *
 * One colour is chosen per project; the tints the interface needs are worked
 * out here so any colour stays legible: a pale background with dark text that
 * clears WCAG AA against it, a lighter shade that still reads on the navy
 * sidebar, and white or ink text on the colour itself, whichever contrasts
 * more. With no project colour the variables are removed and index.css's
 * brand defaults apply.
 */

type Rgb = [number, number, number];

const HEX = /^#([0-9a-f]{6})$/i;

function parse(hex: string): Rgb | null {
  const match = HEX.exec(hex.trim());
  if (!match) return null;
  const value = parseInt(match[1]!, 16);
  return [(value >> 16) & 255, (value >> 8) & 255, value & 255];
}

const toHex = ([r, g, b]: Rgb) => `#${[r, g, b].map((channel) => Math.round(channel).toString(16).padStart(2, '0')).join('')}`;

const mix = (from: Rgb, to: Rgb, amount: number): Rgb =>
  [0, 1, 2].map((index) => from[index]! + (to[index]! - from[index]!) * amount) as Rgb;

function luminance([r, g, b]: Rgb): number {
  const [lr, lg, lb] = [r, g, b].map((channel) => {
    const value = channel / 255;
    return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
  }) as Rgb;
  return 0.2126 * lr + 0.7152 * lg + 0.0722 * lb;
}

export function contrast(a: Rgb, b: Rgb): number {
  const [light, dark] = [luminance(a), luminance(b)].sort((x, y) => y - x) as [number, number];
  return (light + 0.05) / (dark + 0.05);
}

const WHITE: Rgb = [255, 255, 255];
const BLACK: Rgb = [0, 0, 0];

/** Step towards `to` until `ok` holds, or give up at the last step. */
function until(base: Rgb, to: Rgb, start: number, ok: (colour: Rgb) => boolean): Rgb {
  for (let amount = start; amount <= 0.9; amount += 0.05) {
    const candidate = mix(base, to, amount);
    if (ok(candidate)) return candidate;
  }
  return mix(base, to, 0.9);
}

export function accentShades(hex: string): Record<string, string> | null {
  const base = parse(hex);
  if (!base) return null;

  const navy = parse(brand.navy)!;
  const ink = parse(brand.ink)!;
  const tint = mix(base, WHITE, 0.9);

  return {
    '--accent': toHex(base),
    '--accent-50': toHex(tint),
    // Marks on the navy sidebar: light enough to see.
    '--accent-400': toHex(until(base, WHITE, 0.15, (colour) => contrast(colour, navy) >= 3)),
    '--accent-600': toHex(until(base, BLACK, 0.12, (colour) => contrast(colour, tint) >= 3)),
    // Text on the pale tint.
    '--accent-700': toHex(until(base, BLACK, 0.25, (colour) => contrast(colour, tint) >= 4.5)),
    '--accent-on': contrast(WHITE, base) >= contrast(ink, base) ? neutral.white : brand.ink,
  };
}

const VARIABLES = ['--accent', '--accent-50', '--accent-400', '--accent-600', '--accent-700', '--accent-on'];

/** Apply a project's colour to the whole app, or return to the brand accent with null. */
export function applyAccent(hex: string | null | undefined): void {
  const root = document.documentElement;
  const shades = hex ? accentShades(hex) : null;

  VARIABLES.forEach((name) => (shades ? root.style.setProperty(name, shades[name]!) : root.style.removeProperty(name)));

  // The phone's browser bar follows too.
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', shades?.['--accent-600'] ?? brand.navy);
}
